import React, { useCallback, useEffect, useMemo, useState } from "react";
import Head from "next/head";
import {
  MessageSquareMore,
  Bell,
  FileText,
  Clock5,
  CircleCheck,
} from "lucide-react";
import { FeatureCard } from "@/components/ui/FeatureCard";
import { SignInButton, SignUpButton } from "@clerk/nextjs";
import { Button } from "@/components/ui/Button";
import { FormDropzone } from "@/components/forms/FormDropzone";
import { countryList } from "@/constants/countries";
import { FormikContext, useFormik } from "formik";
import { FormInput } from "@/components/forms/FormInput";
import { FormSelect } from "@/components/forms/FormSelect";
import { landingValidationSchema } from "@/components/features/landing/validations";
import { useAppDispatch, useResetStoreState } from "@/hooks";
import { checkEmailExistsAPI, signUpUserAPI } from "@/apis/users";
import Image from "next/image";
import toast from "react-hot-toast";
import { FormTextArea } from "@/components/forms/FormTextArea";
import { initialSignupFormValues } from "@/components/features/landing/fixtures";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/Alert";
import { debounce } from "lodash";
import { getControlAPI } from "@/apis/controls";
import { actions } from "@/redux/authentication";
import { PublicLayout } from "@/components/layout/PublicLayout/PublicLayout";

const LandingPage = () => {
  const dispatch = useAppDispatch();

  useResetStoreState();

  const features = [
    {
      icon: CircleCheck,
      title: "Talent Assessment",
      description: "Take a quick test to find your talents and strengths",
    },
    {
      icon: Clock5,
      title: "Career Matching",
      description:
        "Find out what careers and jobs you are best suited to based on your talents",
    },
    {
      icon: FileText,
      title: "Resume Enhancement",
      description:
        "Upload your resume FREE for expert feedback, reformatting, and career-specific versions",
    },
    {
      icon: Bell,
      title: "Job Alerts",
      description: "Register for upcoming jobs in your chosen career path",
    },
    {
      icon: MessageSquareMore,
      title: "AI Career Assistant",
      description:
        "Get instant answers about your resume, jobs, and career paths from our AI Chatbot",
    },
  ];

  const [controlledValues, setControlledValues] = useState(null);
  const [howDidYouHearLabel, setHowDidYouHearLabel] = useState("");
  const [alert, setAlert] = useState(null);

  const handleSignUp = () => {
    dispatch(actions.callSetSignUpDetails(formikBag.values));
  };

  const handleSubmit = async (values) => {
    try {
      const response = await signUpUserAPI(values);
      if (response.success) {
        toast("Launching in a few days! We will email you.", {
          icon: "🚀",
        });
        const resdata = await uploadResumeAPI(values.file);
        formikBag.resetForm();
      } else {
        toast.error(
          response.message || "Something went wrong! Please try again later."
        );
      }
    } catch (error) {
      // Now this will only trigger for actual network errors
      toast.error("Network error! Please check your connection.");
    }
  };

  const formikBag = useFormik({
    initialValues: initialSignupFormValues,
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema: landingValidationSchema,
    onSubmit: handleSubmit,
  });

  const validateEmail = async (email) => {
    try {
      const response = await checkEmailExistsAPI({ email });
      if (response?.data?.exists) {
        setAlert({
          variant: "destructive",
          title: "Error",
          description: "Email already exists!",
        });
      } else {
        setAlert(null);
      }
    } catch (error) {
      console.error("Error validating email:", error);
    }
  };

  const debouncedValidateEmail = useCallback(
    debounce((email) => validateEmail(email), 500),
    []
  );

  useEffect(() => {
    if (formikBag.values.email) {
      debouncedValidateEmail(formikBag.values.email);
    }
  }, [formikBag.values.email]);

  useEffect(() => {
    if (controlledValues?.howDidYouHear && document.referrer) {
      const referrerUrl = document.referrer.toLowerCase();

      formikBag.setFieldValue("documentReferrer", referrerUrl);

      // Special case for t.co (Twitter/X.com)
      if (referrerUrl.includes("t.co")) {
        const xOption = controlledValues.howDidYouHear.find((option) =>
          option.value.toLowerCase().includes("x.com")
        );
        if (xOption) {
          signUpFormik.setFieldValue("howDidYouHear", xOption.value);
          return;
        }
      }

      // Find matching option from dropdown
      const matchingOption = controlledValues.howDidYouHear.find((option) => {
        const optionValue = option.value.toLowerCase();
        // Check if the referrer URL includes the option value
        // or if the option value includes the referrer domain
        return (
          referrerUrl.includes(optionValue) ||
          (optionValue.includes("http") &&
            referrerUrl.includes(new URL(optionValue).hostname))
        );
      });

      if (matchingOption) {
        formikBag.setFieldValue("howDidYouHear", matchingOption.value);
      }
    }
  }, [controlledValues?.howDidYouHear, document.referrer]);

  const fetchControlledValues = async () => {
    try {
      const response = await getControlAPI();
      if (response.success) {
        const extractedControls =
          response.data?.[0]?.options?.map(({ label, value }) => ({
            label,
            value,
          })) || [];
        setControlledValues({ howDidYouHear: extractedControls });
        setHowDidYouHearLabel(response.data?.[0]?.label);
      } else {
        toast.error("Failed to load controlled values");
      }
    } catch (error) {
      console.error("Error fetching controlled values:", error);
      toast.error("Failed to load controlled values");
    }
  };

  useEffect(() => {
    fetchControlledValues();
  }, []);

  const isFormValid = useMemo(
    () => landingValidationSchema.isValidSync(formikBag.values) && !alert,
    [alert, formikBag.values]
  );

  return (
    <PublicLayout>
      <main className="max-w-6xl mx-auto px-4 py-6 sm:py-12">
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Introducing FreeTalentAssessment
          </h1>
        </div>

        <div className="grid max-w-4xl m-auto lg:grid-cols-3 md:grid-cols-2 gap-3 sm:gap-6 mb-16">
          {features.map((feature, index) => (
            <FeatureCard
              key={index}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
            />
          ))}
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-8 max-w-4xl m-auto">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">
            Sign Up Now - It's Free!
          </h2>

          <FormikContext.Provider value={formikBag}>
            {alert && (
              <Alert variant={alert.variant}>
                <AlertTitle>{alert.title}</AlertTitle>
                <AlertDescription>{alert.description}</AlertDescription>
              </Alert>
            )}

            <form
              onSubmit={formikBag.handleSubmit}
              className="space-y-4 sm:space-y-6"
            >
              <div className="grid gap-4 sm:grid-cols-2 sm:gap-6">
                <FormInput
                  label="First Name"
                  name="firstName"
                  placeholder="John"
                  isRequired
                />
                <FormInput
                  label="Last Name"
                  name="lastName"
                  placeholder="Doe"
                  isRequired
                />
              </div>
              <FormInput
                label="Email Address"
                name="email"
                type="email"
                placeholder="email@mail.com"
                isRequired
              />
              <div className="grid gap-4 sm:grid-cols-2 sm:gap-6">
                <FormSelect
                  label="Country"
                  name="country"
                  options={countryList}
                  placeholder="Select country..."
                  isRequired
                />
                <FormInput
                  label="Postal/Zip Code"
                  name="zipCode"
                  placeholder="90210"
                  isRequired
                />
              </div>

              {controlledValues && (
                <FormSelect
                  label={howDidYouHearLabel}
                  name="howDidYouHear"
                  options={controlledValues.howDidYouHear}
                  value={formikBag.values.howDidYouHear}
                  handleSelectChange={() => {
                    formikBag.setFieldValue("referrerName", "");
                    formikBag.setFieldValue("referrerEmail", "");
                    formikBag.setFieldValue("otherDetails", "");
                  }}
                />
              )}

              {formikBag.values.howDidYouHear === "referral" && (
                <div className="space-y-4">
                  <FormInput
                    label="Referral Name"
                    name="referrerName"
                    placeholder="Enter referral name"
                    isRequired
                  />
                  <FormInput
                    label="Referral Email"
                    name="referrerEmail"
                    placeholder="Enter referral email"
                    type="email"
                    isRequired
                  />
                </div>
              )}

              {formikBag.values.howDidYouHear === "other" && (
                <FormTextArea
                  label="Additional Details"
                  name="otherDetails"
                  placeholder="Please provide more details"
                  rows={4}
                  isRequired
                />
              )}

              <FormDropzone name="resumeFile" />

              {isFormValid ? (
                <SignUpButton
                  mode="modal"
                  forceRedirectUrl="/dashboard"
                  initialValues={{
                    emailAddress: formikBag.values.email,
                  }}
                >
                  <Button
                    type="button"
                    className="w-full"
                    onClick={handleSignUp}
                  >
                    Submit
                  </Button>
                </SignUpButton>
              ) : (
                <Button
                  type="button"
                  className="w-full"
                  onClick={formikBag.submitForm}
                >
                  Submit
                </Button>
              )}
            </form>
          </FormikContext.Provider>
        </div>
      </main>
    </PublicLayout>
  );
};

export default LandingPage;
